import React from 'react';
import { Card, CardContent, CardMedia, Typography, Chip, Box } from '@mui/material';
import { NewsPost } from '../types';
import { useNavigate } from 'react-router-dom';
import PushPinIcon from '@mui/icons-material/PushPin';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VisibilityIcon from '@mui/icons-material/Visibility';

interface NewsCardProps {
  post: NewsPost;
  showFullContent?: boolean;
}

const NewsCard: React.FC<NewsCardProps> = ({ post, showFullContent = false }) => {
  const navigate = useNavigate();

  const getPriorityColor = (priority: string): 'error' | 'warning' | 'info' | 'default' => {
    switch (priority) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'normal':
        return 'info';
      default:
        return 'default';
    }
  };

  const getPriorityLabel = (priority: string): string => {
    return priority.charAt(0).toUpperCase() + priority.slice(1);
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '';

    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInHours = diffInMs / (1000 * 60 * 60);
    const diffInDays = diffInHours / 24;

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
      return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
    } else if (diffInHours < 24) {
      const hours = Math.floor(diffInHours);
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else if (diffInDays < 7) {
      const days = Math.floor(diffInDays);
      return `${days} day${days !== 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'America/New_York'
      });
    }
  };

  const handleClick = () => {
    if (!showFullContent) {
      navigate(`/news/${post.id}`);
    }
  };

  return (
    <Card
      sx={{
        cursor: showFullContent ? 'default' : 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': showFullContent ? {} : {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        },
        border: post.is_pinned ? '2px solid' : 'none',
        borderColor: post.is_pinned ? 'primary.main' : 'transparent',
        position: 'relative',
      }}
      onClick={handleClick}
    >
      {post.is_pinned && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            bgcolor: 'primary.main',
            borderRadius: '50%',
            p: 0.5,
          }}
        >
          <PushPinIcon sx={{ color: 'white', fontSize: 20 }} />
        </Box>
      )}

      {post.image_url && (
        <CardMedia
          component="img"
          height="200"
          image={post.image_url}
          alt={post.title}
          sx={{ objectFit: 'cover' }}
        />
      )}

      <CardContent>
        {/* Priority and Categories */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip
            label={getPriorityLabel(post.priority)}
            color={getPriorityColor(post.priority)}
            size="small"
          />
          {post.categories.map((category, index) => (
            <Chip
              key={index}
              label={category}
              size="small"
              variant="outlined"
            />
          ))}
        </Box>

        {/* Title */}
        <Typography variant="h5" component="h2" gutterBottom>
          {post.title}
        </Typography>

        {/* Summary */}
        {post.summary && (
          <Typography variant="body2" color="text.secondary" paragraph>
            {post.summary}
          </Typography>
        )}

        {/* Full Content (if showFullContent is true) */}
        {showFullContent && (
          <Typography
            variant="body1"
            sx={{ mt: 2, mb: 2, whiteSpace: 'pre-wrap' }}
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        )}

        {/* Footer Info */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PersonIcon fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              {post.author.first_name} {post.author.last_name}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTimeIcon fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary">
                {formatDate(post.publish_date || post.created_at)}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <VisibilityIcon fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary">
                {post.views_count} {post.views_count === 1 ? 'view' : 'views'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default NewsCard;
